import { useEffect, useId, useMemo, useRef, useState } from 'react'
import './SearchSelect.css'

/**
 * Generic over the value type so the same control serves an id picker and a
 * filter whose values are composite strings ("2:5" = "missing PUC certificate").
 * Defaults to number, which is what most callers pass.
 */
export interface SearchOption<T extends string | number = number> {
  value: T
  label: string
  /** Shown greyed after the label — "(machinery)", a site's district, and so on. */
  hint?: string
}

/**
 * A select you can type into.
 *
 * A native <select> is fine for four options and useless for the site list, which
 * is long enough that finding "Mahabaleshwar" means scrolling a popup with no way
 * to search it. Browsers do type-ahead on the first letters only, so "bhawan"
 * finds nothing.
 *
 * Built rather than reached for: a <datalist> would give the search for free but
 * carries no value/label distinction, so the form would have to match typed text
 * back to an id and would silently accept anything that did not match. This
 * always resolves to a real id or to nothing.
 *
 * The input holds the SEARCH text, not the value. On blur it is reset to the
 * selected option's label, so a half-typed query never sits in the box looking
 * like a choice that was made.
 */
export function SearchSelect<T extends string | number = number>({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  emptyLabel,
  disabled,
  id,
}: {
  options: SearchOption<T>[]
  value: T | ''
  onChange: (value: T | '') => void
  placeholder?: string
  /**
   * Offered as the first row when the field may legitimately be left unset.
   * Omit it where the field always holds a value — an obligation's state is
   * always one of the four, and "none" would be a fifth that does not exist.
   */
  emptyLabel?: string
  disabled?: boolean
  id?: string
}) {
  const selected = options.find((o) => o.value === value)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [active, setActive] = useState(0)

  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return options

    // Substring, not prefix. "cargo" should find "LMV - CARGO", which is exactly
    // what the browser's own type-ahead cannot do.
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(query) || (o.hint?.toLowerCase().includes(query) ?? false),
    )
  }, [options, search])

  // Rows including the optional "unassigned" row, which is a real choice and has
  // to be reachable by keyboard like any other.
  const rows: (SearchOption<T> | null)[] = emptyLabel ? [null, ...filtered] : filtered

  useEffect(() => setActive(0), [search, open])

  // Pointer-down rather than click: a click outside that lands on another control
  // would otherwise fire before this closes, and the list would eat the first tap.
  useEffect(() => {
    if (!open) return

    function onDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }

    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  })

  function close() {
    setOpen(false)
    setSearch('')
  }

  function choose(option: SearchOption<T> | null) {
    onChange(option ? option.value : '')
    close()
    inputRef.current?.focus()
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      setActive((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1
        // Wraps, so holding one arrow key cannot strand the user at an end with
        // no visible reason why nothing is moving.
        return (next + rows.length) % rows.length
      })
      return
    }

    if (event.key === 'Enter' && open) {
      event.preventDefault()
      if (rows.length > 0) choose(rows[active] ?? null)
      return
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault()
      // Stopped here so Escape closes the list without also closing the dialog or
      // leaving the page behind it.
      event.stopPropagation()
      close()
      return
    }

    if (event.key === 'Tab') close()
  }

  return (
    <div className="ssel" ref={rootRef}>
      <input
        id={id}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && rows.length > 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        disabled={disabled}
        className="ssel__input"
        // The box shows what is selected until the user starts typing, at which
        // point it shows what they are typing. Two jobs, but the alternative is a
        // separate search box inside the popup, which is more chrome for no gain.
        //
        // With nothing selected it shows emptyLabel as a VALUE, not a placeholder.
        // A placeholder renders in muted ink, so "All sites" came out grey where
        // the native <select> this replaced drew it as an ordinary selected option
        // in full ink — which made the whole filter row look faded.
        value={open ? search : (selected?.label ?? emptyLabel ?? '')}
        placeholder={placeholder}
        onChange={(e) => {
          setSearch(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          // Only when focus actually leaves the widget — clicking a row moves
          // focus within it and must not close before the choice registers.
          if (!rootRef.current?.contains(e.relatedTarget as Node)) close()
        }}
        onKeyDown={onKeyDown}
      />

      <button
        type="button"
        className="ssel__toggle"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        onClick={() => {
          setOpen((was) => !was)
          inputRef.current?.focus()
        }}
      >
        ▾
      </button>

      {open && (
        <ul className="ssel__list" id={listId} role="listbox">
          {rows.length === 0 && <li className="ssel__none">No match for “{search}”</li>}

          {rows.map((option, index) => (
            <li
              key={option?.value ?? 'none'}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option ? option.value === value : value === ''}
              className={`ssel__row${index === active ? ' ssel__row--active' : ''}${
                option === null ? ' ssel__row--none' : ''
              }`}
              // pointerdown, not click: blur fires first on click and would close
              // the list before the choice landed.
              onPointerDown={(e) => {
                e.preventDefault()
                choose(option)
              }}
              onPointerEnter={() => setActive(index)}
            >
              <span>{option ? option.label : emptyLabel}</span>
              {option?.hint && <span className="ssel__hint">{option.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
